import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useCallback,
  useEffect,
  useState
} from 'react';
import { InfiniteData, useMutation } from '@tanstack/react-query';
import { TreeDataNode } from 'antd';
import { useLocation, useSearchParams } from 'react-router-dom';

import { documents } from 'api';
import { DirectoriesParams } from 'api/documents.types';
import { useQueryParams } from 'hooks';
import { Folder, FolderFill } from 'icons';
import { $Object, Directory } from 'types';
import { withValues } from 'utils';
import { UserContext } from './UserContext';

interface FoldersContextInterface {
  formattedData: TreeDataNode[];
  setFormattedData: Dispatch<SetStateAction<TreeDataNode[]>>;
  fetchNextFolderPage: (forcePage?: number, foundId?: number) => void;
  isFolderMutateLoading: boolean;
  renderCount: number;
  setData: Dispatch<SetStateAction<InfiniteData<TreeDataNode> | undefined>>;
  icon: TreeDataNode['icon'];
  folderData?: Directory;
  setSelectedTreeFilter: (id: number | string, filters: $Object) => (number | string) | any[];
  handleExpand: (variables: DirectoriesParams & { id: number }) => Promise<void>;
  expandKey: number | null;
  clearDirectoryCache: (id?: number) => void;
}

const icon: TreeDataNode['icon'] = ({ selected }) => (selected ? <FolderFill /> : <Folder />);

const PARAMS_SELECTED_TREE = '--selectedTree';

export const FoldersContext = createContext<FoldersContextInterface>({
  formattedData: [],
  setFormattedData: () => null,
  fetchNextFolderPage: () => null,
  isFolderMutateLoading: false,
  renderCount: 0,
  setData: () => null,
  icon,
  setSelectedTreeFilter: () => [],
  handleExpand: () => Promise.resolve(),
  expandKey: null,
  clearDirectoryCache: () => null
});

const returnChildrenTreeNode = (directory: Directory, formattedData: TreeDataNode[]) => {
  const recursive = (children?: TreeDataNode[]): TreeDataNode | undefined => {
    return children?.find((treeData) => {
      const found = `${treeData.key}` === `${directory.id}`;

      if (
        treeData?.children?.length &&
        found &&
        treeData?.children?.length !== directory.children.length + 1
      ) {
        treeData.children = [];
      }

      const isEmptyArray = treeData?.children?.[0]?.key === `key-${directory.id}`;

      if (treeData?.children?.length && !isEmptyArray) {
        return recursive(treeData.children);
      }

      const hasChildrenList = !!directory?.children?.length;

      if (found) {
        treeData.children = [
          ...directory.children.map(({ id, name }) => ({
            key: id,
            title: name,
            icon,
            className: `key-id--${id}`,
            children: [{ key: `key-${id}`, style: { display: 'none' } }]
          })),
          ...(hasChildrenList
            ? [
                {
                  key: `parent--${directory.id}--treedata--${treeData.key}`,
                  className: `parent--${directory.id}--treedata--${treeData.key}`,
                  title: <div data-id={`${directory.id}`} />,
                  style: { height: 0, visibility: 'hidden' } as React.CSSProperties
                }
              ]
            : [])
        ];
      }

      return found;
    });
  };

  recursive(formattedData);

  return formattedData;
};

export const FoldersProvider = ({ children }: PropsWithChildren) => {
  const { data: user } = useContext(UserContext);
  const [infiniteMutationData, setInfiniteMutationData] = useState<Record<string, Omit<Directory, 'children'> & { children: Directory[][] }>>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [infinitePage, setInfinitePage] = useState<Record<string, number>>({});
  const [formattedData, setFormattedData] = useState<TreeDataNode[]>([]);
  const { pathname } = useLocation();
  const [renderCount, setRenderCount] = useState(0);
  const [data, setData] = useState<InfiniteData<TreeDataNode>>();
  const [expandKey, setExpandKey] = useState<number | null>(null);

  const queryParams = useQueryParams();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSearchParams] = useSearchParams();

  const selectedKey = queryParams.directory as number;

  // Ensure folder-related state does not leak across account boundaries
  useEffect(() => {
    setInfiniteMutationData({});
    setInfinitePage({});
    setFormattedData([]);
    setRenderCount(0);
    setData(undefined);
    setExpandKey(null);
  }, [user?.id]);

  const {
    mutate: folderMutate,
    mutateAsync: folderMutateAsync,
    isLoading: isFolderMutateLoading
  } = useMutation({
    mutationFn: ({ id, ...variables }: DirectoriesParams & { id: number }) =>
      documents.getDirectoryById(id, {
        per_page: 10,
        search: queryParams.search,
        ordering: queryParams.ordering,
        partner_ids: queryParams.partner_ids,
        ...variables
      }),
    onSuccess: (data, variables) => {
      setInfiniteMutationData((value) => {
        if (value[variables.id]) {
          // Check if this is a pagination request (has cursor) or initial request
          if (variables.cursor) {
            // Pagination request - append to existing children
            value[variables.id].next_cursor = data.next_cursor;
            value[variables.id].previous_cursor = data.previous_cursor;
            value[variables.id].children.push(data.children);
          } else {
            // Initial request - replace children to prevent duplicates
            value[variables.id].next_cursor = data.next_cursor;
            value[variables.id].previous_cursor = data.previous_cursor;
            value[variables.id].children = [data.children];
          }
          return { ...value };
        } else {
          return { ...value, [variables.id]: { ...data, children: [data.children] } };
        }
      });
    }
  });

  useEffect(() => {
    if (!selectedKey) return;

    // Check if we already have data for this directory to prevent duplicate requests
    // Only skip if we have data AND the search/ordering/partner_ids haven't changed
    const hasExistingData = infiniteMutationData[selectedKey] && infiniteMutationData[selectedKey].children.length > 0;
    const hasFilterChanges = queryParams.search !== undefined || queryParams.ordering !== undefined || queryParams.partner_ids !== undefined;
    
    if (hasExistingData && !hasFilterChanges) {
      return;
    }

    if (infiniteMutationData[selectedKey]) {
      setInfiniteMutationData((value) => ({
        ...value,
        [selectedKey]: { ...value[selectedKey], children: [] }
      }));
    }

    setInfinitePage(() => {
      folderMutate({ id: +selectedKey! });

      return { [selectedKey]: 1 };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, queryParams.search, queryParams.ordering, queryParams.partner_ids]);

  const folderData = useCallback(
    (id = selectedKey) =>
      id && infiniteMutationData[id]
        ? {
            ...infiniteMutationData[id],
            children: infiniteMutationData[id].children.flat(2)
          }
        : undefined,
    [infiniteMutationData, selectedKey]
  );

  const fetchNextFolderPage = (forcePage?: number, foundId?: number) => {
    const id = foundId || selectedKey;

    if (!id || !folderData(id)) return;
    setExpandKey(id);

    const nextCursor = infiniteMutationData[id]?.next_cursor;

    if (!nextCursor) return;

    setInfinitePage((value) => {
      const page = (value[id] || 1) + 1;
      folderMutate({ id: +id!, cursor: nextCursor });

      return { [id]: page };
    });
  };

  const handleExpand = async (variables: DirectoriesParams & { id: number }) => {
    try {
      // Check if we already have data for this directory to prevent duplicate requests
      if (infiniteMutationData[variables.id] && infiniteMutationData[variables.id].children.length > 0) {
        const directoryData = {
          ...infiniteMutationData[variables.id],
          children: infiniteMutationData[variables.id].children.flat(2)
        };
        const newData = returnChildrenTreeNode(directoryData, formattedData);
        setFormattedData(newData);
        setExpandKey(variables.id);
        return;
      }

      const data = await folderMutateAsync(variables);

      const newData = returnChildrenTreeNode(data, formattedData);

      setFormattedData(newData);
      setExpandKey(variables.id);
    } catch {
      setSearchParams(
        withValues({
          ...queryParams,
          directory: null,
          [PARAMS_SELECTED_TREE]: null
        })
      );
    }
  };

  useEffect(() => {
    const queryKey = queryParams[PARAMS_SELECTED_TREE];

    const queryKeys = Array.isArray(queryKey) ? queryKey : [queryKey];

    if (!(queryKey && queryKeys?.length) || !selectedKey) return;

    let dataSet = formattedData;

    queryKeys.forEach(async (id) => {
      // Check if we already have data for this directory to prevent duplicate requests
      if (infiniteMutationData[id] && infiniteMutationData[id].children.length > 0) {
        const directoryData = {
          ...infiniteMutationData[id],
          children: infiniteMutationData[id].children.flat(2)
        };
        const newData = returnChildrenTreeNode(directoryData, dataSet);
        dataSet = newData;
        setRenderCount((i) => ++i);
        return;
      }

      try {
        const data = await folderMutateAsync({ id });

        const newData = returnChildrenTreeNode(data, dataSet);

        dataSet = newData;
        setRenderCount((i) => ++i);
      } catch {
        setSearchParams(
          withValues({
            ...queryParams,
            directory: null,
            [PARAMS_SELECTED_TREE]: null
          })
        );
      }
    });

    setFormattedData(dataSet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, formattedData]);

  useEffect(() => {
    const id = expandKey || selectedKey;
    if (!folderData(id) || !id) return;
    const newData = returnChildrenTreeNode(folderData(id)!, formattedData);

    newData && setFormattedData(newData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderData]);

  useEffect(() => {
    selectedKey && setExpandKey(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  useEffect(() => {
    if (!data) return;

    setFormattedData((treeData) => [
      ...treeData,
      ...(data?.pages.filter(
        ({ key }) => !treeData.length || !treeData.some((value) => value.key === key)
      ) || [])
    ]);
  }, [data]);

  const setSelectedTreeFilter = (id: number | string, filters: $Object) => {
    const param = Array.isArray(filters[PARAMS_SELECTED_TREE])
      ? [...filters[PARAMS_SELECTED_TREE], id]
      : filters[PARAMS_SELECTED_TREE]
        ? [filters[PARAMS_SELECTED_TREE], id]
        : id;

    return Array.isArray(param) ? Array.from(new Set(param)) : param;
  };

  const clearDirectoryCache = (id?: number) => {
    if (id) {
      setInfiniteMutationData((value) => {
        const newValue = { ...value };
        delete newValue[id];
        return newValue;
      });
    } else {
      setInfiniteMutationData({});
    }
  };

  return (
    <FoldersContext.Provider
      value={{
        formattedData,
        setFormattedData,
        fetchNextFolderPage,
        isFolderMutateLoading,
        renderCount,
        setData,
        icon,
        folderData: folderData(),
        setSelectedTreeFilter,
        handleExpand,
        expandKey,
        clearDirectoryCache
      }}
    >
      {children}
    </FoldersContext.Provider>
  );
};
